import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Shield, 
  ArrowLeft,
  Heart,
  AlertTriangle,
  Ban,
  MessageCircle,
  Camera,
  Users,
  Flag,
  ThumbsUp,
  ThumbsDown,
  Scale,
  Eye,
  UserX
} from "lucide-react";

const CommunityGuidelinesPage = () => {
  const currentDate = "Mars 2026";

  const principles = [
    {
      icon: Heart,
      title: "Bienveillance",
      description: "Évaluez avec honnêteté et respect. Une évaluation doit être constructive, pas destructive.",
      color: "text-pink-500",
      bgColor: "bg-pink-100"
    },
    {
      icon: Eye,
      title: "Authenticité",
      description: "Ne notez que des personnes que vous avez réellement rencontrées. Les fausses évaluations sont interdites.",
      color: "text-blue-500",
      bgColor: "bg-blue-100"
    },
    {
      icon: Scale,
      title: "Équité",
      description: "Basez vos évaluations sur des faits, pas sur des préjugés. La discrimination est strictement interdite.",
      color: "text-purple-500",
      bgColor: "bg-purple-100"
    },
    {
      icon: Shield,
      title: "Sécurité",
      description: "Protégez-vous et protégez les autres. Signalez tout comportement suspect ou dangereux.",
      color: "text-green-500",
      bgColor: "bg-green-100"
    }
  ];

  const rules = [
    {
      category: "Évaluations",
      icon: ThumbsUp,
      color: "text-green-600",
      bgColor: "bg-green-100",
      allowed: [
        "Noter quelqu'un après une interaction réelle",
        "Fournir des preuves vérifiables (photos, vidéos)",
        "Décrire factuellement ce qui s'est passé",
        "Évaluer le comportement, pas la personne",
        "Modifier ou supprimer votre évaluation si nécessaire"
      ],
      forbidden: [
        "Noter quelqu'un sans l'avoir rencontré",
        "Utiliser des preuves truquées ou hors contexte",
        "Inventer des faits ou exagérer",
        "Évaluer sur des critères discriminatoires",
        "Créer plusieurs comptes pour manipuler les notes"
      ]
    },
    {
      category: "Communication",
      icon: MessageCircle,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
      allowed: [
        "Rédiger des commentaires constructifs",
        "Expliquer le contexte de votre évaluation",
        "Répondre poliment aux questions",
        "Signaler un contenu inapproprié"
      ],
      forbidden: [
        "Insulter ou menacer quelqu'un",
        "Harceler ou intimider",
        "Publier des informations personnelles (doxxing)",
        "Utiliser un langage discriminatoire"
      ]
    },
    {
      category: "Contenu",
      icon: Camera,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
      allowed: [
        "Photos/vidéos prises avec consentement",
        "Captures d'écran de conversations (si pertinent)",
        "Documents prouvant une transaction",
        "Témoignages écrits avec permission"
      ],
      forbidden: [
        "Contenu à caractère sexuel",
        "Violence graphique",
        "Contenu illégal (drogues, armes, etc.)",
        "Photos/vidéos prises sans consentement",
        "Contenu protégé par le droit d'auteur"
      ]
    },
    {
      category: "Identité",
      icon: Users,
      color: "text-amber-600",
      bgColor: "bg-amber-100",
      allowed: [
        "Utiliser votre vrai nom et photo",
        "Créer un seul compte par personne",
        "Vérifier votre profil avec une photo"
      ],
      forbidden: [
        "Créer un faux profil",
        "Usurper l'identité de quelqu'un",
        "Utiliser une photo de quelqu'un d'autre",
        "Créer plusieurs comptes",
        "Vendre ou acheter un compte"
      ]
    }
  ];

  const sanctions = [
    {
      level: "Avertissement",
      description: "Premier manquement mineur aux règles",
      icon: AlertTriangle,
      color: "text-amber-500",
      bgColor: "bg-amber-50"
    },
    {
      level: "Restriction temporaire",
      description: "Suspension du droit d'évaluer pendant 7-30 jours",
      icon: Ban,
      color: "text-orange-500",
      bgColor: "bg-orange-50"
    },
    {
      level: "Bannissement",
      description: "Violation grave ou récidive - compte supprimé définitivement",
      icon: UserX,
      color: "text-red-500",
      bgColor: "bg-red-50"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FFF0F5]">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md border-b border-pink-100">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-extrabold text-xl" style={{ fontFamily: 'Outfit' }}>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-200 to-pink-100 flex items-center justify-center">
              <Shield size={18} className="text-[#FF3366]" fill="#FF3366" />
            </div>
            <span className="text-slate-900">SHARE</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="rounded-full">
              <ArrowLeft size={18} className="mr-2" />
              Retour
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="space-y-8">
          {/* Title */}
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF3366]/10 text-[#FF3366] text-sm font-medium">
              <Shield size={16} />
              Règles de la communauté
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Règles Communautaires
            </h1>
            <p className="text-slate-500">Version en vigueur : {currentDate}</p>
            <p className="text-slate-600 max-w-2xl mx-auto">
              SHARE est une communauté basée sur la confiance et la bienveillance. 
              Ces règles garantissent une expérience positive pour tous.
            </p>
          </div>

          {/* Principles */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-900 text-center">Nos 4 Principes Fondamentaux</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {principles.map((principle, index) => (
                <Card key={index} className="guardian-card">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-full ${principle.bgColor} flex items-center justify-center flex-shrink-0`}>
                        <principle.icon className={principle.color} size={24} />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">{principle.title}</h3>
                        <p className="text-slate-600 text-sm">{principle.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Detailed Rules */}
          <section className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-900 text-center">Règles Détaillées</h2>
            
            {rules.map((rule, index) => (
              <Card key={index} className="guardian-card overflow-hidden">
                <div className={`${rule.bgColor} px-6 py-4 flex items-center gap-3`}>
                  <rule.icon className={rule.color} size={24} />
                  <h3 className="text-lg font-bold text-slate-900">{rule.category}</h3>
                </div>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Allowed */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <ThumbsUp className="text-green-500" size={18} />
                        <h4 className="font-bold text-green-700">Autorisé</h4>
                      </div>
                      <ul className="space-y-2">
                        {rule.allowed.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                            <span className="text-green-500 mt-0.5">✓</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {/* Forbidden */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <ThumbsDown className="text-red-500" size={18} />
                        <h4 className="font-bold text-red-700">Interdit</h4>
                      </div>
                      <ul className="space-y-2">
                        {rule.forbidden.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                            <span className="text-red-500 mt-0.5">✗</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </section>

          {/* Sanctions */}
          <section className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-900 text-center">Sanctions en cas de non-respect</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {sanctions.map((sanction, index) => (
                <Card key={index} className={`${sanction.bgColor} border-none`}>
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 rounded-full bg-white flex items-center justify-center mx-auto mb-4`}>
                      <sanction.icon className={sanction.color} size={28} />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">{sanction.level}</h3>
                    <p className="text-slate-600 text-sm">{sanction.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Reporting */}
          <section className="space-y-4">
            <Card className="guardian-card border-[#FF3366]/20 bg-[#FF3366]/5">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#FF3366] flex items-center justify-center flex-shrink-0">
                    <Flag className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">Comment signaler un problème ?</h3>
                    <p className="text-slate-600 mb-4">
                      Si vous êtes témoin ou victime d'un comportement contraire à nos règles :
                    </p>
                    <ol className="list-decimal list-inside text-slate-600 space-y-2">
                      <li>Accédez au profil de la personne concernée</li>
                      <li>Cliquez sur le bouton "Signaler"</li>
                      <li>Sélectionnez la raison du signalement</li>
                      <li>Ajoutez des détails si nécessaire</li>
                      <li>Envoyez le signalement</li>
                    </ol>
                    <p className="text-slate-500 text-sm mt-4">
                      Notre équipe examine tous les signalements sous 24-48h.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Contact */}
          <section className="text-center space-y-4">
            <p className="text-slate-600">
              Des questions sur nos règles communautaires ?
            </p>
            <p className="text-slate-600">
              Contactez-nous : <a href="mailto:contact@rateshare.fr" className="text-[#FF3366] hover:underline font-medium">contact@rateshare.fr</a>
            </p>
          </section>

          {/* Back button */}
          <div className="text-center pt-8">
            <Link to="/">
              <Button className="btn-guardian rounded-full">
                <ArrowLeft size={18} className="mr-2" />
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-pink-100 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-slate-500 text-sm">
          <p>© 2026 RATE⭐SHARE - Le GPS de la Bienveillance</p>
          <div className="flex justify-center gap-4 mt-4">
            <Link to="/terms" className="hover:text-[#FF3366]">CGU</Link>
            <Link to="/community-guidelines" className="hover:text-[#FF3366]">Règles</Link>
            <Link to="/privacy" className="hover:text-[#FF3366]">Confidentialité</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CommunityGuidelinesPage;
